/**
 * Uploads [{ path, file }] to a module: sign a batch of URLs, PUT the files
 * straight to cloud storage (a few at a time), then record the ones that
 * arrived. Reports every file's state so the panel can show progress.
 */

import { completeUpload, errorMessage, startUpload, uploadFile } from "./studyMaterialsApi";

const BATCH_SIZE = 200; // files signed and recorded per request
const PARALLEL = 4;

/** onUpdate(path, patch) gets { status, progress, error } changes. */
export async function uploadAll(moduleId, items, onUpdate, kind = "template") {
  const results = { uploaded: 0, failed: [], skipped: [] };
  for (let start = 0; start < items.length; start += BATCH_SIZE) {
    const chunk = items.slice(start, start + BATCH_SIZE);
    const byPath = new Map(chunk.map((item) => [item.path, item]));
    let signed;
    try {
      signed = await startUpload(moduleId, chunk.map((item) => ({
        path: item.path,
        size: item.file.size,
        content_type: item.file.type || "application/octet-stream",
      })), kind);
    } catch (error) {
      const message = errorMessage(error, "Couldn't start the upload");
      chunk.forEach((item) => { onUpdate(item.path, { status: "failed", error: message }); results.failed.push(item); });
      continue;
    }

    signed.skipped.forEach((s) => {
      onUpdate(s.path, { status: "skipped", error: s.reason });
      results.skipped.push(s);
    });

    const arrived = [];
    const queue = [...signed.files];
    const worker = async () => {
      while (queue.length) {
        const target = queue.shift();
        const item = byPath.get(target.path); // paths were normalized like the server does
        if (!item) continue;
        onUpdate(item.path, { status: "uploading", progress: 0 });
        try {
          await uploadFile(target.upload_url, item.file, target.content_type,
            (progress) => onUpdate(item.path, { progress }));
          arrived.push({ ...target, item });
        } catch (error) {
          onUpdate(item.path, { status: "failed", error: errorMessage(error, "Upload failed") });
          results.failed.push(item);
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(PARALLEL, queue.length) }, worker));

    if (!arrived.length) continue;
    try {
      const done = await completeUpload(moduleId, signed.batch,
        arrived.map(({ path, key, content_type: contentType }) => ({ path, key, content_type: contentType })), kind);
      const missing = new Set(done.missing);
      arrived.forEach(({ path, item }) => {
        if (missing.has(path)) {
          onUpdate(item.path, { status: "failed", error: "Didn't reach storage" });
          results.failed.push(item);
        } else {
          onUpdate(item.path, { status: "done", progress: 100 });
          results.uploaded += 1;
        }
      });
    } catch (error) {
      const message = errorMessage(error, "Couldn't save the upload");
      arrived.forEach(({ item }) => { onUpdate(item.path, { status: "failed", error: message }); results.failed.push(item); });
    }
  }
  return results;
}

/** Reads a drag-and-drop into [{ path, file }], walking dropped folders. */
export async function itemsFromDrop(dataTransfer) {
  const entries = [...(dataTransfer.items || [])]
    .map((item) => (item.webkitGetAsEntry ? item.webkitGetAsEntry() : null))
    .filter(Boolean);
  if (!entries.length) return [...(dataTransfer.files || [])].map((file) => ({ path: file.name, file }));

  const out = [];
  const walk = async (entry, prefix) => {
    if (entry.isFile) {
      const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
      out.push({ path: prefix + file.name, file });
      return;
    }
    const reader = entry.createReader();
    for (;;) {
      const batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
      if (!batch.length) break;
      for (const child of batch) await walk(child, `${prefix}${entry.name}/`);
    }
  };
  for (const entry of entries) await walk(entry, "");
  return out;
}
